// src/screens/superadmin/analytics/Analytics.jsx
import React, { useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { 
  FaDownload, 
  
  FaShoppingCart, 
  FaPercent 
} from 'react-icons/fa';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Button from '../../../components/general/button/Button';
import Pagination from '../../../components/general/pagination/Pagination';
import StatCard from '../../../components/admin/statcard/StatCard';
import '../../../assets/css/admin/analytics/Analytics.css';
import { FaArrowTrendUp } from 'react-icons/fa6';
import AdminLayout from '../../../layouts/AdminLayout';

const Analytics = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [dateRange, setDateRange] = useState('last-6-months');

  const statsData = [
    { 
      icon:FaArrowTrendUp, 
      iconBg: 'linear-gradient(135deg, #e8f5f0, #d4ede3)', 
      iconColor: '#0ac46c', 
      label: 'Total Revenue', 
      value: '₹2.3M', 
      change: '+18%' 
    },
    { 
      icon: FaShoppingCart, 
      iconBg: 'linear-gradient(135deg, #e3f2fd, #bbdefb)', 
      iconColor: '#2196f3', 
      label: 'Total Orders', 
      value: '12,840', 
      change: '+12%' 
    },
    { 
      icon: FaPercent, 
      iconBg: 'linear-gradient(135deg, #fff3e0, #ffe0b2)', 
      iconColor: '#ff9800', 
      label: 'Growth Rate', 
      value: '+16.7%', 
      change: 'MoM' 
    },
  ];

  // Revenue & Orders Trend Data (Last 6 Months)
  const trendData = [
    { month: 'Sep', revenue: 280000, orders: 1850 },
    { month: 'Oct', revenue: 320000, orders: 2100 },
    { month: 'Nov', revenue: 350000, orders: 2300 },
    { month: 'Dec', revenue: 390000, orders: 2500 },
    { month: 'Jan', revenue: 420000, orders: 2700 },
    { month: 'Feb', revenue: 450000, orders: 2900 },
  ];

  // Category Performance Data
  const categoryData = [
    { name: 'Seeds', value: 35, color: '#0ac46c' },
    { name: 'Tools', value: 28, color: '#2196f3' },
    { name: 'Fertilizers', value: 22, color: '#ff9800' },
    { name: 'Others', value: 15, color: '#9c27b0' },
  ];

  // Regional Sales Data
  const regionalData = [
    { region: 'Punjab', sales: 950234 },
    { region: 'Sindh', sales: 820145 },
    { region: 'KPK', sales: 680920 },
    { region: 'Balochistan', sales: 420680 },
    { region: 'Others', sales: 280340 },
  ];

  // Order Status Distribution
  const orderStatusData = [
    { name: 'Delivered', value: 11920, color: '#0ac46c' },
    { name: 'Pending', value: 450, color: '#ff9800' },
    { name: 'Cancelled', value: 470, color: '#f44336' },
  ];

  // Top Selling Products
  const topProducts = [
    { id: 1, image: 'https://images.unsplash.com/photo-1587049352846-4a222e784dd4?w=60&h=60&fit=crop', name: 'Agri-Quik Fertilizer', category: 'Seeds', revenue: '₹1,601,646', units: '95222 units' },
    { id: 2, image: 'https://images.unsplash.com/photo-1592841200221-a6898f307baa?w=60&h=60&fit=crop', name: 'Tomato Genesis Pro', category: 'Tools', revenue: '₹1,245,788', units: '78445 units' },
    { id: 3, image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=60&h=60&fit=crop', name: 'Uratex Heavy Phosphate', category: 'Fertilizers', revenue: '₹998,234', units: '65221 units' },
  ];

  const handleExport = (format) => {
    console.log('Exporting report as:', format);
  };

  return (
    <AdminLayout>
    <div>
      {/* Page Header */}
      <div className="ksa-analytics-page-header">
        <div>
          <h1 className="ksa-layout-page-title">Business Analytics & Reports</h1>
          <p className="ksa-layout-page-subtitle">Track key metrics, performance data, and business growth.</p>
        </div>
        <div className="ksa-analytics-header-actions">
          <select 
            className="ksa-analytics-date-filter"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="last-7-days">Last 7 Days</option>
            <option value="last-30-days">Last 30 Days</option>
            <option value="last-6-months">Last 6 Months</option>
            <option value="last-year">Last Year</option>
          </select>
          <Button 
            variant="primary" 
            size="medium"
            icon={<FaDownload />}
            iconPosition="left"
            onClick={() => handleExport('pdf')}
          >
            Download Report
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <Row className="g-3 mb-4">
        {statsData.map((stat, idx) => (
          <Col key={idx} xs={12} md={4}>
            <StatCard {...stat} />
          </Col>
        ))}
      </Row>

      {/* Revenue & Orders Trend Chart */}
      <div className="ksa-card mb-4">
        <div className="ksa-analytics-chart-header">
          <div>
            <h3 className="ksa-analytics-chart-title">Revenue & Orders Trend</h3>
            <p className="ksa-analytics-chart-subtitle">Performance over the last 6 months</p>
          </div>
        </div>
        <div className="ksa-analytics-chart-container">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" stroke="#666" />
              <YAxis stroke="#666" />
              <Tooltip 
                contentStyle={{ 
                  background: '#fff', 
                  border: '1px solid #f0f0f0', 
                  borderRadius: '8px',
                  fontFamily: 'Poppins, sans-serif'
                }} 
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="revenue" 
                stroke="#0ac46c" 
                strokeWidth={3}
                fill="url(#colorRevenue)"
                name="Revenue (PKR)"
              />
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0ac46c" stopOpacity={0.1}/>
                  <stop offset="95%" stopColor="#0ac46c" stopOpacity={0}/>
                </linearGradient>
              </defs>
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row */}
      <Row className="g-4 mb-4">
        {/* Category Performance */}
        <Col xs={12} lg={6}>
          <div className="ksa-card">
            <div className="ksa-analytics-chart-header">
              <h3 className="ksa-analytics-chart-title">Category Performance</h3>
            </div>
            <div className="ksa-analytics-chart-container" style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      background: '#fff', 
                      border: '1px solid #f0f0f0', 
                      borderRadius: '8px',
                      fontFamily: 'Poppins, sans-serif'
                    }} 
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="ksa-analytics-legend">
                {categoryData.map((item, idx) => (
                  <div key={idx} className="ksa-analytics-legend-item">
                    <span className="ksa-analytics-legend-dot" style={{ background: item.color }}></span>
                    <span className="ksa-analytics-legend-label">{item.name}</span>
                    <span className="ksa-analytics-legend-value">{item.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Col>

        {/* Order Status Distribution */}
        <Col xs={12} lg={6}>
          <div className="ksa-card">
            <div className="ksa-analytics-chart-header">
              <h3 className="ksa-analytics-chart-title">Order Status Pie Chart</h3>
            </div>
            <div className="ksa-analytics-chart-container" style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={orderStatusData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="value"
                    label
                  >
                    {orderStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      background: '#fff', 
                      border: '1px solid #f0f0f0', 
                      borderRadius: '8px',
                      fontFamily: 'Poppins, sans-serif'
                    }} 
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="ksa-analytics-legend">
                {orderStatusData.map((item, idx) => (
                  <div key={idx} className="ksa-analytics-legend-item">
                    <span className="ksa-analytics-legend-dot" style={{ background: item.color }}></span>
                    <span className="ksa-analytics-legend-label">{item.name}</span>
                    <span className="ksa-analytics-legend-value">{item.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Regional Sales Performance */}
      <div className="ksa-card mb-4">
        <div className="ksa-analytics-chart-header">
          <h3 className="ksa-analytics-chart-title">Regional Sales Performance</h3>
        </div>
        <div className="ksa-analytics-chart-container">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={regionalData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="region" stroke="#666" />
              <YAxis stroke="#666" />
              <Tooltip 
                contentStyle={{ 
                  background: '#fff', 
                  border: '1px solid #f0f0f0', 
                  borderRadius: '8px',
                  fontFamily: 'Poppins, sans-serif'
                }} 
              />
              <Bar dataKey="sales" fill="#0ac46c" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Selling Products Table */}
      <div className="ksa-card">
        <div className="ksa-analytics-table-header">
          <h3 className="ksa-analytics-table-title">Top Selling Products</h3>
          <span className="ksa-analytics-table-subtitle">Most revenue-generating items</span>
        </div>
        <div className="ksa-analytics-table-wrapper">
        <table className="ksa-analytics-table">
          <thead>
            <tr>
              <th>PRODUCT</th>
              <th>CATEGORY</th>
              <th>REVENUE</th>
              <th>UNITS SOLD</th>
            </tr>
          </thead>
          <tbody>
            {topProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((product) => (
              <tr key={product.id}>
                <td>
                  <div className="ksa-analytics-product">
                    <img src={product.image} alt={product.name} />
                    <span>{product.name}</span>
                  </div>
                </td>
                <td className="ksa-analytics-category">{product.category}</td>
                <td className="ksa-analytics-revenue">{product.revenue}</td>
                <td className="ksa-analytics-units">{product.units}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {/* Pagination */}
        <Pagination
          totalItems={topProducts.length}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </div>
    </div>
    </AdminLayout>
  );
};

export default Analytics;